/**
 * The HTTP client every feature shares.
 *
 * One place that knows the error envelope, so **every** failure arrives as an `ApiError` carrying a
 * stable `code` and a `correlation_id`. Screens branch on the code and print the id; they never
 * parse a message, because messages are localized and codes are not
 * (docs/engineering/07-api-conventions.md).
 *
 * Credentials are sent on every request: the session is an opaque `__Host-` cookie, never a token
 * this code could hold, log, or accidentally put in a URL.
 */

import { isApiErrorBody, type ErrorCode } from '@pius/contracts';

const BASE = '/api/v1';

export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode,
    message: string,
    readonly correlationId?: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: init.body ? { 'content-type': 'application/json' } : {},
    ...init,
  });

  if (res.status === 204) return undefined as T;

  const payload: unknown = await res.json().catch(() => undefined);

  if (!res.ok) {
    if (isApiErrorBody(payload)) {
      throw new ApiError(
        payload.error.code,
        payload.error.message,
        payload.error.correlation_id,
        payload.error.details,
      );
    }
    // A response that is not even shaped like an error — a proxy timeout, an HTML page. Reported as
    // an internal error rather than surfacing whatever text came back.
    throw new ApiError('internal_error', 'Something went wrong. Please try again.');
  }

  return payload as T;
}
